import { types, getEnv, flow, SnapshotOrInstance, Instance, SnapshotOut } from 'mobx-state-tree';
import { ICommonAppEnvWithSBBaseApi, SportsBettingApiResult } from '../../index';
import { ISportsBettingBaseApi, CancelError } from '../../api/apiBase';
import {
    IGroupedApiCtx, CashoutAcceptedChangeTypeUI, CashoutAcceptedChangeTypeUIForStore,
    ProgressStateModelForStore, errorIsCancel, ProgressStateModel,
    ITicketDetailResponse, ITicketSelectionDetailResponse, TicketType, StakeType,
    protoUtils, TicketState,
    cashOutDtos, TelemetryServiceDataType, ITicketCashOutParams, TicketCashOutResponse
} from 'eTip--common';
import { observable } from 'mobx';
import { createTicketSelectionEventStatusResultStoreFromLiveData, createTicketSelectionEventStatusResultStoreFromProto, TicketSelectionEventStatusStore, TicketSelectionStore, TicketSelectionEventStatusResultStore, ITicketSelectionEventStatusAsStore } from './ticketDetailSelection';
import { TicketBetStore } from './ticketDetailBet';
import { currency, pointsCurrency, translationForMts, translateForMts } from 'easit--localization';
import { OfferType, ResultType, ShowResultStyle, TicketSelectionEvalState, ITicketCancelResponse, TicketCancelResult, IReuseAliveSelectionResponse, IReuseAliveSelection } from '../../api/proto/apiContract';
import { CalculationsForMyTicketStore, CalculationsForPublicTicketStore, CalculationsBaseStore } from './ticketCalculations';
import { MoneyWithCurrency } from './mtsTicket';
import { gAService } from '../../services/windowWithGa';
import * as routing from 'easit--routing';
import { ApiLiveEventsStatusItemData, ApiLiveEventsStatusResponse } from '../../dtos/apiLiveEventsDtos';

export const defaultTicketDetailStore = (): Instance<typeof TicketDetailStore> => {
    let data: any = {};
    resetValuesInStore(data as Instance<typeof TicketDetailStore>, true);
    return data;
}

const CashOutDecreaseRatePercentInitValue = 5;

const resetValuesInStore = (store: SnapshotOrInstance<typeof TicketDetailStore>, canResetCalculations: boolean): void => {
    store.id = '';
    store.isNotLegacy = true;
    store.legacyContent = null;
    store.offerType = OfferType.Prematch; //as a default value
    store.winToPay = 0;
    store.potentialWin = 0;
    store.placementDate = null;
    store.rate = 0;
    store.ticketType = TicketType.None;
    store.stakeType = StakeType.All;
    store.stake = 0;
    store.stakePoints = null;
    store.handingFee = 0;
    store.handingFeePoints = null;
    store.error = null;
    store.selections = observable([]);
    store.bets = observable([]);
    store.number = '';
    store.publicUrl = "";

    store.ticketState = TicketState.None;

    store.state = <ProgressStateModel>'notInitialized';
    store.cancelState = ProgressStateModel.NotInitialized;
    store.cashoutAcceptedChangeType = CashoutAcceptedChangeTypeUI.None;
    store.decreaseAgreement = false;
    store.decreaseValue = CashOutDecreaseRatePercentInitValue;

    store.cashOutState = ProgressStateModel.NotInitialized;
    store.reuseOddsState = ProgressStateModel.NotInitialized;
    store.cashOut = null;
    store.cashOutErrors = null;

    if (canResetCalculations) {
        store.calculationsForPublicTicket = null;
        store.calculationsForMyTicket = null;
    }
}

export enum TicketDetailSourceType { Unknown = "unknown", MyTicket = "myTicket", PublicTicket = "publicTicket", TicketByNumber = "ticketByNumber" };

export const TicketDetailTypeForStore = types.enumeration('TicketDetailTypeForStore', [TicketDetailSourceType.Unknown, TicketDetailSourceType.MyTicket, TicketDetailSourceType.PublicTicket, TicketDetailSourceType.TicketByNumber]);


export const TicketDetailStore = types
    .model('ticketDetailStore', {
        id: types.string, //must be string since it may be long (bigger than Number)
        isNotLegacy: types.optional(types.boolean, true), // better name to have true?
        legacyContent: types.maybeNull(types.string),
        evalState: types.maybeNull(types.number), //EvalState
        ticketState: types.maybeNull(types.number), //TicketState
        offerType: types.number, //OfferType
        winToPay: types.maybeNull(types.number),
        potentialWin: types.maybeNull(types.number),
        finalWin: types.maybeNull(types.number),
        placementDate: types.maybeNull(types.Date),
        rate: types.maybeNull(types.number),
        ticketType: types.number, //TicketType
        stake: types.number,
        stakePoints: types.maybeNull(types.number), //null means not set
        stakeType: types.number, //StakeType
        handingFee: types.number,
        handingFeePoints: types.maybeNull(types.number),
        error: types.maybeNull(types.string),
        selections: types.array(TicketSelectionStore),
        bets: types.array(TicketBetStore),
        number: types.maybeNull(types.string),
        productFee: types.maybeNull(MoneyWithCurrency),
        productName: types.maybeNull(types.string),
        oddsRateAdvantage: types.maybeNull(types.number),
        rateAdvantageFactor: types.maybeNull(types.number),
        customerNickName: types.maybeNull(types.string),
        prizePaidDate: types.maybeNull(types.Date),

        calculationsForMyTicket: types.maybeNull(CalculationsForMyTicketStore),
        calculationsForPublicTicket: types.maybeNull(CalculationsForPublicTicketStore),

        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        cashoutAcceptedChangeType: types.optional(CashoutAcceptedChangeTypeUIForStore, CashoutAcceptedChangeTypeUI.None),
        decreaseAgreement: types.boolean,
        decreaseValue: types.number,

        cashOut: types.maybeNull(types.number),
        cashOutState: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        cashOutErrors: types.maybeNull(types.array(types.string)),

        sourceType: types.optional(TicketDetailTypeForStore, TicketDetailSourceType.Unknown),
        publicUrl: types.string,

        dateCancelationLimit: types.maybeNull(types.Date),
        cancelState: types.maybeNull(ProgressStateModelForStore),
        cancelMessage: types.maybeNull(types.string),
        reuseOddsState: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        reuseOddsMessage: types.maybeNull(types.string)
    })
    .views(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const prepareBackUrl = (): string => {
            try {
                let searchParams = routing.getSearchParams();
                if (searchParams.has("urlref")) {
                    let urlRef = new window.URL(searchParams.get("urlref"));
                    let domains = __DomainsForUrlRefSplitByComma__.split(",");
                    for (var i = 0; i < domains.length; i++) {
                        if (urlRef.hostname.endsWith(domains[i])) {
                            return searchParams.get("urlref");
                        }
                    }
                }
            } catch {
            }
            return null;
        }

        let backUrl: string | null;

        return {
            get isCashoutEnabled() {
                return self.cashOut > 0;
            },

            get selectionsWithoutBankerCnt() {
                return self.selections.filter(selection => !selection.isBanker).length;
            },

            get decreaseAggreementPercents() {
                return [CashOutDecreaseRatePercentInitValue, 10, 20, 30];
            },

            get calculations(): Instance<typeof CalculationsBaseStore> {
                switch (self.sourceType) {
                    case TicketDetailSourceType.MyTicket: return self.calculationsForMyTicket;
                    case TicketDetailSourceType.PublicTicket:
                    case TicketDetailSourceType.TicketByNumber:
                        return self.calculationsForPublicTicket;
                }
                return null;
            },

            get visualDelay(): number {
                return env.settingsFromApi.timeForCashout / 1000;
            },

            get isCountdownEnabled(): boolean {
                return self.offerType === OfferType.Live;
            },

            get publicUrlHash(): string {
                let indx = self.publicUrl.lastIndexOf("/"); //same below in setData
                if (indx > 0) {
                    return self.publicUrl.substring(indx + 1);
                }
                return null;
            },

            get canShowNewFeatureScore(): boolean {
                return env.settings.canShowNewFeatureScore;
            },

            get canShowLiveEventStatusInTicketDetail(): boolean {
                return env.settings.canShowLiveEventStatusInTicketDetail;
            },

            get canShowFinalScoreInfo(): boolean {
                return env.settings.canShowNewFeatureScore && self.selections.some(selection => selection.state !== TicketSelectionEvalState.None && selection.score /* no score is null */);
            },

            get canShowCancelButton(): boolean {
                return self.dateCancelationLimit && self.sourceType == TicketDetailSourceType.MyTicket;
            },

            get canShowReuseOddsButton(): boolean {
                return self.selections.some(x => x.state === TicketSelectionEvalState.None)
            },

            get backUrl(): string {
                if (!backUrl) {
                    backUrl = prepareBackUrl();
                }
                return backUrl;
            },

            get liveEventStatusRefreshTimeMs() {
                return env.settings.ticketDetailLiveScoreRefreshTimeMs;
            }
        }
    })
    .actions(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        let synchroFullLoadCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
        const tickerNumberRegExp = /^\s*\d+\s*$/;
        const t = translationForMts();

        const setData = (ticket: ITicketDetailResponse, liveEventStatusProvider: (source: ITicketSelectionDetailResponse) => Instance<typeof TicketSelectionEventStatusStore> | null): void => {
            //env.loggingService.info(ticket);
            self.id = ticket.Base.Id.toString();
            self.legacyContent = null;
            self.isNotLegacy = true;
            self.offerType = ticket.OfferType;
            self.ticketState = ticket.State;
            self.finalWin = ticket.PrizeEvalValue;
            self.potentialWin = ticket.Base.PrizeEvalPotentionalValue;
            self.winToPay = ticket.ToPay;
            self.placementDate = protoUtils.protoTimeStamp2Date(ticket.PlacementDate);
            self.rate = ticket.Base.Rate;
            self.ticketType = ticket.Base.TicketType;
            self.stakeType = ticket.Base.StakeType;
            self.handingFee = ticket.Base.HandlingFeeValue;
            self.handingFeePoints = ticket.Base.HandlingFeePointsValue ? ticket.Base.HandlingFeePointsValue : null; // 0 => null
            self.evalState = ticket.EvalState;
            self.cashOut = ticket.CashOut;
            self.number = ticket.Base.Number;
            self.publicUrl = ticket.PublicUrl;
            self.productName = ticket.AccountTypeName;

            if (env.settings.canShowExtendedTicketDetail) {
                self.customerNickName = ticket.CustomerNickName;
                self.prizePaidDate = ticket.PrizePaidDate ? protoUtils.protoTimeStamp2Date(ticket.PrizePaidDate) : null;
            }

            self.stake = ticket.Base.Stake;
            self.stakePoints = ticket.Base.StakePoints ? ticket.Base.StakePoints.Value : null;
            if (ticket.Base.ProductFee) {
                self.productFee = MoneyWithCurrency.create(<SnapshotOut<typeof MoneyWithCurrency>>{
                    amount: ticket.Base.ProductFee.Amount, currency: ticket.Base.ProductFee.IsMoney ? currency : pointsCurrency
                });
            } else {
                self.productFee = null;
            }
            self.oddsRateAdvantage = ticket.Base.OddsRateAdvantage ? ticket.Base.OddsRateAdvantage.Value : null;
            self.rateAdvantageFactor = ticket.Base.RateAdvantageFactor ? ticket.Base.RateAdvantageFactor.Value : null;

            if (self.sourceType == TicketDetailSourceType.TicketByNumber) {
                let indx = self.publicUrl.lastIndexOf("/"); //same in views
                if (indx > 0) {
                    self.calculationsForPublicTicket.setPublicId(self.publicUrl.substr(indx + 1));
                }

            }

            self.dateCancelationLimit = ticket.CustomerStornoTicketLimit ? protoUtils.protoTimeStamp2Date(ticket.CustomerStornoTicketLimit) : null;

            setSelections(ticket.Selections, liveEventStatusProvider);
            setBets(ticket);
        }

        const setSelections = (selections: ITicketSelectionDetailResponse[], liveEventStatusProvider: (source: ITicketSelectionDetailResponse) => Instance<typeof TicketSelectionEventStatusStore> | null) => {
            self.selections = observable([]);
            selections.forEach(selection => {
                self.selections.push(TicketSelectionStore.create(<SnapshotOut<typeof TicketSelectionStore>>{
                    id: selection.Base.Id.toString(),
                    state: selection.EvalState,
                    fixtureId: selection.Base.FixtureId,
                    fixtureDate: selection.Base.Text ? protoUtils.protoTimeStamp2Date(selection.Base.Text.FixtureDate).getTime() : null,
                    fixtureName: selection.Base.Text ? selection.Base.Text.FixtureName : '',
                    isBanker: selection.Base.IsBanker,
                    number: selection.Base.Text ? selection.Base.Text.Number : 0,
                    rate: selection.Base.Rate,
                    resultNote: selection.ResultNote,
                    sportName: selection.Base.Text ? selection.Base.Text.SportName : '',
                    marketName: selection.Base.Text ? selection.Base.Text.MarketName : '',
                    tipName: selection.Base.Text ? selection.Base.Text.SelectionName : '',
                    symbol: selection.Base.Symbol,
                    correctSelectionNames: selection.CorrectSelectionNames,
                    regionName: selection.Base.Text.RegionName,
                    tournamentName: selection.Base.Text.TournamentName,
                    score: (selection.ShowResultStyle === ShowResultStyle.SelectedResults) && selection.Scores && selection.Scores.length ? selection.Scores.map(score => score.Score).reduce((previousValue, currentValue) => `${previousValue} ${currentValue}`) : null,
                    resultText: null,
                    offerType: selection.Base.OfferType,
                    liveEventStatus: env.settings.canShowLiveEventStatusInTicketDetail ?
                        (liveEventStatusProvider
                            ? liveEventStatusProvider(selection) : selection.LiveEventStatus ? TicketSelectionEventStatusStore.create(<SnapshotOut<typeof TicketSelectionEventStatusStore>>{
                                mainScore: selection.LiveEventStatus.MainScore,
                                stateName: selection.LiveEventStatus.StateName,
                                stateTime: selection.LiveEventStatus.StateTime?.Value,
                                importantResults: selection.LiveEventStatus.ImportantResults?.map(result => createTicketSelectionEventStatusResultStoreFromProto(result))
                            })
                                : null)
                        : null
                }))
            })
        }

        const setBets = (ticket: ITicketDetailResponse) => {
            const selectionsCnt = ticket.Selections.filter(selection => !selection.Base.IsBanker).length;
            self.bets = observable([]);
            ticket.Base.Bets.forEach(bet => {
                self.bets.push(TicketBetStore.create(<SnapshotOut<typeof TicketBetStore>>{
                    combinationCount: bet.CombinationCount,
                    selectedSystem: bet.SelectedSystem,
                    selectionCount: selectionsCnt,
                    systemStake: bet.SystemStake,
                    unitStake: bet.UnitStake
                }))
            })
        }

        const setLegacyData = (data: string): void => {
            self.legacyContent = data;
        }

        const isLegacyTicketId = (ticketId: string): boolean => {
            //legacy tickets end at 2200000000 - 1, see sql proc_TicketCreate
            // not possible to convert id to number since it may be longer!
            if (ticketId.length > 10) {
                return false;
            }
            if (ticketId.length < 10) {
                return true;
            }
            const num1 = Number(ticketId[0]);
            if (num1 > 2) {
                return false;
            }
            if (num1 < 2) {
                return true;
            }
            return (Number(ticketId[1]) < 2);
        }

        const isLegacyTicketNumber = (ticketNumber: string): boolean => {
            if (ticketNumber.length < 4) {
                return false;
            }
            let ticketId = ticketNumber.substr(3, ticketNumber.length - 4);
            return isLegacyTicketId(ticketId);
        }

        let lastLoadedLiveEventStatusForRefresh: Map<number, ITicketSelectionEventStatusAsStore>; // map to set live main scores from after regular reload. Api in GetTicketDetail provides older data and it may show older score.
        const loadMyTicketAsync: (ticketId: string, isReload?: boolean) => Promise<void> = flow(function* (ticketId: string, isReload?: boolean) {
            if (!isReload) {
                resetValuesInStore(self, true);
                self.legacyContent = null;
                lastLoadedLiveEventStatusForRefresh = null;
            }
            self.id = ticketId;

            if (isLegacyTicketId(ticketId)) {
                self.isNotLegacy = false;
                yield loadDataRawAsync(TicketDetailSourceType.MyTicket,
                    (api: ISportsBettingBaseApi): Promise<string | null> => {
                        return api.legacyTicketDetailAsync({ ticketId: ticketId }, env.settings.legacySBUrl);
                    },
                    (data: string) => { setLegacyData(data); })
                return;
            }

            self.calculationsForMyTicket = CalculationsForMyTicketStore.create(<SnapshotOut<typeof CalculationsForMyTicketStore>>{
                ticketId: ticketId
            }, env);
            self.calculationsForPublicTicket = null;
            yield loadDataRawAsync(TicketDetailSourceType.MyTicket,
                (api: ISportsBettingBaseApi): Promise<ITicketDetailResponse> => {
                    return api.ticketDetailAsync({ ticketId: ticketId, withEventStatus: !isReload });
                },
                (data: ITicketDetailResponse) => { setData(data, isReload && lastLoadedLiveEventStatusForRefresh ? liveEventStatusProvider : null); },
                ticketId,
                isReload);
            if (!isReload) {
                lastLoadedLiveEventStatusForRefresh = new Map<number, Instance<typeof TicketSelectionEventStatusStore>>();
                self.selections.forEach(sel => {
                    if (sel.liveEventStatus) {
                        lastLoadedLiveEventStatusForRefresh.set(sel.fixtureId, createCopyOfSelectionEventStatusForLastForRefresh(sel));
                    }
                });
            }
        });

        const liveEventStatusProvider = (source: ITicketSelectionDetailResponse): Instance<typeof TicketSelectionEventStatusStore> | null => {
            if (!env.settings.canShowLiveEventStatusInTicketDetail) {
                return null;
            }

            if (lastLoadedLiveEventStatusForRefresh) {
                let fixtureId = Number(source.Base.FixtureId);
                if (lastLoadedLiveEventStatusForRefresh.has(fixtureId)) {
                    let lastLoadedStatusData = lastLoadedLiveEventStatusForRefresh.get(fixtureId);
                    return createNewEventStatusStore(lastLoadedStatusData);
                }
            }
            return null;
        }

        const createNewEventStatusStore = (source: ITicketSelectionEventStatusAsStore) => {
            return TicketSelectionEventStatusStore.create(<SnapshotOut<typeof TicketSelectionEventStatusStore>>{
                mainScore: source.mainScore,
                stateName: source.stateName,
                stateTime: source.stateTime,
                importantResults: source.importantResults
            });
        };

        const createCopyOfSelectionEventStatusForLastForRefresh = (sel: Instance<typeof TicketSelectionStore>) => {
            return {
                ...sel.liveEventStatus, importantResults: sel.liveEventStatus.importantResults.map((res: Instance<typeof TicketSelectionEventStatusResultStore>) => { return { ...res } })
            }
        }

        const getTicketSelectionsRawAsync: (loader: (ctx: IGroupedApiCtx<ISportsBettingBaseApi>) => Promise<IReuseAliveSelectionResponse>) => Promise<IReuseAliveSelectionResponse> = flow(function* (loader: (ctx: IGroupedApiCtx<ISportsBettingBaseApi>) => Promise<IReuseAliveSelectionResponse>) {
            let ctx: IGroupedApiCtx<ISportsBettingBaseApi> = env.api.open();
            try {
                let data = yield loader(ctx);
                if (data) {
                    return data;
                }
                return null;
            }
            finally {
                ctx.close();
            }
        });

        const getTicketSelectionsAsync: () => Promise<IReuseAliveSelectionResponse> = flow(function* () {
            return getTicketSelectionsRawAsync((ctx: IGroupedApiCtx<ISportsBettingBaseApi>) => {
                return ctx.api.reuseAliveSelectionsAsync({ ticketId: self.id, withEventStatus: false });
            });
        });

        const getPublicTicketSelectionsAsync: () => Promise<IReuseAliveSelectionResponse> = flow(function* () {
            return getTicketSelectionsRawAsync((ctx: IGroupedApiCtx<ISportsBettingBaseApi>) => {
                return ctx.api.reusePublicAliveSelectionsAsync({ publicId: self.publicUrlHash, withEventStatus: false });
            });
        });

        const loadPublicTicketAsync: (publicTicketId: string) => Promise<void> = flow(function* (publicTicketId: string) {
            resetValuesInStore(self, true);
            self.calculationsForPublicTicket = CalculationsForPublicTicketStore.create(<SnapshotOut<typeof CalculationsForPublicTicketStore>>{
                publicTicketId: publicTicketId
            }, env);
            self.calculationsForMyTicket = null;
            yield loadDataRawAsync(TicketDetailSourceType.PublicTicket,
                (api: ISportsBettingBaseApi): Promise<ITicketDetailResponse> => {
                    return api.publicTicketDetailAsync({ publicId: publicTicketId, withEventStatus: true }); // expected no reload
                },
                (data: ITicketDetailResponse) => {
                    if (__IsMTSTipos__) {
                        if (data && !data.Base) { //indicates legacy ticket for Tipos
                            self.id = "";
                            self.isNotLegacy = false;
                            return;
                        }
                    }
                    setData(data, null);
                })
        });

        const loadLegacyPublicTicketAsync: (legacyPublicTicketId: string) => Promise<void> = flow(function* (legacyPublicTicketId: string) {
            resetValuesInStore(self, true);
            self.id = "";
            self.legacyContent = legacyPublicTicketId;
            self.isNotLegacy = false;
            yield loadDataRawAsync(TicketDetailSourceType.PublicTicket,
                (api: ISportsBettingBaseApi): Promise<string> => {
                    return api.legacyTicketDetailAsync({ publicId: legacyPublicTicketId }, env.settings.legacySBUrl);
                },
                (data: string) => { setLegacyData(data); });
        });

        const loadFindTicketByNumberAsync: (ticketNumber: string) => Promise<void> = flow(function* (ticketNumber: string) {
            resetValuesInStore(self, true);
            self.legacyContent = null;

            if (isLegacyTicketNumber(ticketNumber)) {
                self.isNotLegacy = false;
                yield loadDataRawAsync(TicketDetailSourceType.PublicTicket,
                    (api: ISportsBettingBaseApi): Promise<string> => {
                        return api.legacyTicketDetailAsync({ ticketNumber: ticketNumber }, env.settings.legacySBUrl);
                    },
                    (data: string) => { setLegacyData(data); });
                return;
            }

            self.calculationsForPublicTicket = CalculationsForPublicTicketStore.create(<SnapshotOut<typeof CalculationsForPublicTicketStore>>{
                publicTicketId: "" /* need to set it after load! */
            }, env);
            self.calculationsForMyTicket = null;
            yield loadDataRawAsync(TicketDetailSourceType.TicketByNumber,
                (api: ISportsBettingBaseApi): Promise<ITicketDetailResponse> => {
                    return api.ticketByNumberDetailAsync({ ticketNumber: ticketNumber });
                },
                (data: ITicketDetailResponse) => { setData(data, null); },
                ticketNumber)
        });

        let liveEventIdsToUpdateEventStatus: { ids: number[], timestamp?: number | null } | null = null; // instance changes on new load not to update old data

        const loadDataRawAsync: (sourceType: TicketDetailSourceType,
            loader: (api: ISportsBettingBaseApi) => Promise<ITicketDetailResponse | string | null>,
            dataSetter: (data: ITicketDetailResponse | string | null) => void,
            ticketNumber?: string,
            isSilent?: boolean) =>
            Promise<void> = flow(function* (sourceType: TicketDetailSourceType,
                loader: (api: ISportsBettingBaseApi) => Promise<ITicketDetailResponse>,
                dataSetter: (data: ITicketDetailResponse | string | null) => void,
                ticketNumber?: string,
                isSilent?: boolean) {

                if (!isSilent) {
                    liveEventIdsToUpdateEventStatus = null;
                }

                self.sourceType = sourceType;

                if (sourceType != TicketDetailSourceType.PublicTicket && sourceType != TicketDetailSourceType.TicketByNumber && env.loggedUserId <= 0) {
                    self.state = ProgressStateModel.Failed;
                    self.error = t.ticket_Login;
                    return;
                }

                if (someOperationInProgress(false)) {
                    env.loggingService.error("Some operation already in progress!");
                    throw new Error("Some operation already in progress!");
                }

                if (synchroFullLoadCtx && synchroFullLoadCtx.isOpen()) {
                    synchroFullLoadCtx.cancel();
                    synchroFullLoadCtx = null;
                }

                if (!isSilent) {
                    self.state = ProgressStateModel.Loading;
                }
                let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
                try {

                    ctx = env.api.open();
                    synchroFullLoadCtx = ctx;

                    let r: ITicketDetailResponse | string | null = yield loader(ctx.api);
                    if (!r) {
                        if (self.sourceType === TicketDetailSourceType.TicketByNumber) {
                            if (tickerNumberRegExp.test(ticketNumber)) {
                                self.error = translateForMts("ticket_TicketOfNumberNotFound", { number: ticketNumber }).ticket_TicketOfNumberNotFound;
                            } else {
                                self.error = t.ticket_NotFound;
                            }
                        } else {
                            self.error = t.ticket_NotFound;
                        }
                        self.state = ProgressStateModel.Failed;
                        return;
                    }
                    dataSetter(r);
                    if (!isSilent && r && (typeof r != "string")) {
                        liveEventIdsToUpdateEventStatus = { ids: getListOfLiveEventsFromDetail(r) };
                    }
                    self.state = ProgressStateModel.Success;
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    env.loggingService.error(error);
                    self.state = ProgressStateModel.Failed;
                    self.error = t.general_ProgressContentError;
                    throw error;
                } finally {
                    if (synchroFullLoadCtx === ctx) {
                        synchroFullLoadCtx = null;
                    }
                    ctx?.close();
                }
            });

        const getListOfLiveEventsFromDetail = (apiResponse: ITicketDetailResponse): number[] => {

            if (!apiResponse.Selections) {
                return [];
            }
            return apiResponse.Selections.map(v => Number(v.Base.FixtureId));
        }

        /**
         * Returns whether there is something to read - true means to plan the next update (live data), false means nothing to update and no need to plan update.
         */
        const updateLiveEventStatusAsync: () => Promise<boolean> = flow<boolean, ApiLiveEventsStatusResponse[]>(function* () {
            if (!env.settings.canShowLiveEventStatusInTicketDetail) {
                throw new Error("Feature disabled");
            }

            if (!liveEventIdsToUpdateEventStatus || !liveEventIdsToUpdateEventStatus.ids.length) {
                return false;
            }
            let currentLiveEventIdsToUpdateScore = liveEventIdsToUpdateEventStatus;

            let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = env.api.open();
            try {
                let r: ApiLiveEventsStatusResponse = yield ctx.api.getLiveEventsStatusAsync(currentLiveEventIdsToUpdateScore.ids, currentLiveEventIdsToUpdateScore.timestamp);
                if (currentLiveEventIdsToUpdateScore != liveEventIdsToUpdateEventStatus) {
                    throw new CancelError();
                }
                if (!r || r.Result == SportsBettingApiResult.NoChange) {
                    return true;
                }

                currentLiveEventIdsToUpdateScore.timestamp = r.TimeStamp;

                if (!r.ReturnValue || !r.ReturnValue.Items) {
                    return true;
                }

                let idsMap = new Map<number, ApiLiveEventsStatusItemData>(r.ReturnValue.Items.map(item => [item.Id, item]));
                self.selections.forEach(sel => {
                    let found = idsMap.get(sel.fixtureId)
                    if (typeof found !== "undefined") {
                        if (!sel.liveEventStatus) {
                            sel.liveEventStatus = TicketSelectionEventStatusStore.create(<SnapshotOut<typeof TicketSelectionEventStatusStore>>{
                                mainScore: found.MainScore,
                                stateName: found.StateName,
                                stateTime: found.StateTime,
                                importantResults: found.ImportantResults.map(item => createTicketSelectionEventStatusResultStoreFromLiveData(item))
                            });
                        } else {
                            sel.liveEventStatus.setDataFromLiveApi(found);
                        }
                        lastLoadedLiveEventStatusForRefresh?.set(sel.fixtureId, createCopyOfSelectionEventStatusForLastForRefresh(sel));
                    } else {
                        sel.liveEventStatus = null;
                        lastLoadedLiveEventStatusForRefresh?.delete(sel.fixtureId);
                    }
                });
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                throw error;
            } finally {
                ctx?.close();
            }
            return true;
        });

        const someOperationInProgress = (includingLoad: boolean): boolean => {
            return (includingLoad && synchroFullLoadCtx !== null) || self.cancelState == ProgressStateModel.Loading || self.cashOutState == ProgressStateModel.Loading || self.reuseOddsState === ProgressStateModel.Loading;
        }

        const toggleCashoutAcceptedChangeType = (newCashoutAcceptedChangeType: CashoutAcceptedChangeTypeUI): void => {
            if (self.cashoutAcceptedChangeType == newCashoutAcceptedChangeType) {
                self.cashoutAcceptedChangeType = CashoutAcceptedChangeTypeUI.None;
            } else {
                self.cashoutAcceptedChangeType = newCashoutAcceptedChangeType;
            }
        };

        const decreaseAgreementToggle = (): void => {
            self.decreaseAgreement = !self.decreaseAgreement;
        };

        const decreaseValueChange = (newValue: number): void => {
            self.decreaseValue = newValue;
            self.decreaseAgreement = true;
        };

        const setCashOut = (newValue: number): void => {
            if (!someOperationInProgress(true)) {
                self.cashOut = newValue;
            }
        };

        const cashOutChanged = (dataFromCashOutSubscription: cashOutDtos.CashOutResponse): void => {
            //env.loggingService.info("ticketDetail.cashOutChanged");

            const betId = Number(self.id);
            if (dataFromCashOutSubscription && dataFromCashOutSubscription.Bets && dataFromCashOutSubscription.Bets.length) {
                const auxBet = dataFromCashOutSubscription.Bets.find(bet => bet.BetID === betId);
                if (auxBet) {
                    setCashOut(auxBet.CashOut);
                } else {
                    setCashOut(0);
                }
            }
            else {
                setCashOut(0);
            }
        };

        const submitCashOutAsync: () => Promise<void> = flow(function* () {
            if (someOperationInProgress(true)) {
                env.loggingService.error("Some operation already in progress!");
                throw new Error("Some operation already in progress!");
            }

            clearOperationsMessages();

            let started = new Date();
            let telemetryDetailRaw: { id: string, c: number, acc?: boolean, e?: string[] } = { id: self.id, c: self.cashOut };

            try {
                let ctx = env.api.open();
                const apiParams: ITicketCashOutParams = {
                    ticketId: self.id,
                    rateAcceptChangePercentage: __IsMTSTipos__ ? null : (self.decreaseAgreement ? self.decreaseValue : 0),
                    cashout: self.cashOut,
                    cashoutAcceptedChangeType: __IsMTSTipos__ ? self.cashoutAcceptedChangeType : null
                };

                self.cashOutState = ProgressStateModel.Loading;

                const response: TicketCashOutResponse = yield ctx.api.ticketCashOutSubmitAsync(apiParams);
                if (response.ResultType === ResultType.Success) {
                    telemetryDetailRaw["acc"] = true;
                } else {
                    telemetryDetailRaw["e"] = response.Errors;
                }
                env.telemetryService.logAjaxRequest(TelemetryServiceDataType.CashOutSubmit, env.loggedUserId, started, JSON.stringify(telemetryDetailRaw));
                if (response.ResultType === ResultType.Success) {
                    env.loggingService.info('cashout finished');
                    self.cashOutState = ProgressStateModel.Success;
                    self.ticketState = TicketState.Paid;
                    if (response.Detail) {
                        setData(response.Detail, lastLoadedLiveEventStatusForRefresh ? liveEventStatusProvider : null);
                    }
                    env.couponSent({ isForUpdateBalanceOnly: true, isLive: false });
                } else {
                    self.cashOutState = ProgressStateModel.Failed;
                    self.cashOutErrors = observable([...response.Errors]);
                    if (response.CashOut) {
                        self.cashOut = response.CashOut.Value;
                    }
                }
            } catch (error) {
                env.telemetryService.logAjaxFailedRequest(TelemetryServiceDataType.CashOutSubmit, env.loggedUserId, started, JSON.stringify(telemetryDetailRaw), error);
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                self.cashOutState = ProgressStateModel.Failed;
                self.cashOutErrors = observable([t.bet_ErrorDuringCashout]);
                throw error;
            }
        });

        const cancelCouponAsync: () => Promise<void> = flow(function* () {

            if (someOperationInProgress(true)) {
                throw new Error("Cancel already in progress");
            }

            clearOperationsMessages();

            let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
            try {
                ctx = env.api.open();
                self.cancelState = ProgressStateModel.Loading;
                self.cancelMessage = null;
                const r: ITicketCancelResponse = yield ctx.api.ticketCancelAsync({ ticketId: self.id });
                if (r.ResultType === TicketCancelResult.Success) {
                    self.dateCancelationLimit = null;
                    self.ticketState = TicketState.StornoByCustomer;
                    self.cancelState = ProgressStateModel.Success;
                    self.cancelMessage = t.bet_CancelSuccess;
                } else {
                    self.cancelState = ProgressStateModel.Failed;
                    switch (r.ResultType) {
                        case TicketCancelResult.AlreadyCancelled: self.cancelMessage = t.bet_CancelError_AlreadyCancelled; break;
                        case TicketCancelResult.NotForThisPlayer: self.cancelMessage = t.bet_CancelError_NotForThisPlayer; break;
                        case TicketCancelResult.PeriodOver: self.cancelMessage = t.bet_CancelError_PeriodOver; break;
                        default: self.cancelMessage = t.bet_CancelError_GeneralError; break;
                    }
                }
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                self.cancelState = ProgressStateModel.Failed;
                self.cancelMessage = t.bet_CancelError_GeneralError;
            }
        });

        const reuseOddsAsync: (assignSelectionsAsync: (selections: IReuseAliveSelection[], canAnimate: boolean) => Promise<void>, canAnimate: boolean) => Promise<void> = flow(function* (assignSelectionsAsync: (selections: IReuseAliveSelection[], canAnimate: boolean) => Promise<void>, canAnimate: boolean) {

            if (someOperationInProgress(true)) {
                env.loggingService.error("Some operation already in progress!");
                throw new Error("Some operation already in progress!");
            }

            clearOperationsMessages();

            self.reuseOddsMessage = null;
            self.reuseOddsState = ProgressStateModel.Loading;
            try {
                let response: IReuseAliveSelectionResponse = null;

                if (self.sourceType == TicketDetailSourceType.MyTicket) {
                    response = yield getTicketSelectionsAsync();
                } else {
                    response = yield getPublicTicketSelectionsAsync()
                }

                gAService.sendEvent("ReuseTicketOdds", 'click', self.publicUrlHash);

                if (!response?.Selections || response?.Selections.length === 0) {
                    self.reuseOddsMessage = translationForMts().ticket_ReuseOddsNotAllAddedError;
                    self.reuseOddsState = ProgressStateModel.Failed;
                    throw new Error("No selection added");
                }

                yield assignSelectionsAsync(response.Selections, canAnimate);

                if (response.WasAllActiveFound) {
                    self.reuseOddsMessage = null;
                } else {
                    self.reuseOddsMessage = translationForMts().ticket_ReuseOddsNotAllAddedError;
                }
                self.reuseOddsState = ProgressStateModel.Success;
            }
            catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }

                self.reuseOddsState = ProgressStateModel.Failed;
                if (self.reuseOddsMessage === null) {
                    self.reuseOddsMessage = translationForMts().ticket_ReuseOddsGeneralError;
                }
                throw error;
            }
        });

        const clearOperationsMessages = (): void => {

            self.cashOutState = ProgressStateModel.NotInitialized;
            self.cashOutErrors = null;

            self.cancelState = ProgressStateModel.NotInitialized;
            self.cancelMessage = null;

            self.reuseOddsState = ProgressStateModel.NotInitialized;
            self.reuseOddsMessage = null;
        }

        return {
            loadMyTicketAsync,
            loadPublicTicketAsync,
            loadFindTicketByNumberAsync,
            loadLegacyPublicTicketAsync,
            decreaseAgreementToggle,
            toggleCashoutAcceptedChangeType,
            decreaseValueChange,
            cashOutChanged,
            submitCashOutAsync,
            isLegacyTicketId, // TODOHMTS published for Tipos only
            cancelCouponAsync,
            reuseOddsAsync,
            clearOperationsMessages,
            updateLiveEventStatusAsync
        }
    })